import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { v4 as uuidv4 } from "uuid"; // Install uuid library if not already installed
import DOMPurify from "dompurify"; // Install dompurify library if not already installed

const REOFFER_TIMEOUT = 20000; // 20 seconds
let reofferTimeout = null; // Timeout reference

const Room = () => {
    const location = useLocation();
    const userVideo = useRef();
    const userStream = useRef();
    const partnerVideo = useRef();
    const peerRef = useRef();
    const webSocketRef = useRef();
    const peerId = useRef(uuidv4()); // Generate a unique ID for this peer

    // State to track connection status
    const [connectionStatus, setConnectionStatus] = useState("Waiting for peer...");
    const [cameraOn, setCameraOn] = useState(true); // State to track camera status
    const [microphoneOn, setMicrophoneOn] = useState(true); // State to track microphone status
    const [isUserVideoSmall, setIsUserVideoSmall] = useState(false); // State to track video size
    
    const openCamera = async () => {
        const constraints = {
            video: true,
            audio: true,
        };
        navigator.mediaDevices.getUserMedia(constraints).then((stream) => {
            userVideo.current.srcObject = stream;
            userStream.current = stream;
        });
    };

    const toggleCamera = () => {
        if (userStream.current) {
            const videoTrack = userStream.current.getVideoTracks()[0];
            if (videoTrack) {
                videoTrack.enabled = !videoTrack.enabled; // Toggle the video track
                setCameraOn(videoTrack.enabled); // Update the camera status
            }
        }
    };

    const toggleMicrophone = () => {
        if (userStream.current) {
            const audioTrack = userStream.current.getAudioTracks()[0];
            if (audioTrack) {
                audioTrack.enabled = !audioTrack.enabled; // Toggle the audio track
                setMicrophoneOn(audioTrack.enabled); // Update the microphone status
            }
        }
    };

    const toggleUserVideoSize = () => {
        setIsUserVideoSmall((prevState) => !prevState); // Toggle the video size state
    };

    useEffect(() => {
        openCamera().then(async () => {
            const roomID = location.pathname.split("/")[2];
            if (!/^[a-zA-Z0-9_-]+$/.test(roomID)) {
                console.error("Invalid room ID");
                return;
            }

            webSocketRef.current = new WebSocket(`wss://avsecure.vip:8443/join?roomID=${roomID}`);

            webSocketRef.current.addEventListener("open", () => {
                webSocketRef.current.send(JSON.stringify({ type: "join", peerId: peerId.current }));
            });

            webSocketRef.current.addEventListener("message", async (e) => {
                try {
                    const message = JSON.parse(e.data);

                    // Validate message structure
                    if (!message.type || !["join", "offer", "answer", "iceCandidate"].includes(message.type)) {
                        console.error("Invalid message type:", message);
                        return;
                    }

                    if (message.peerId === peerId.current) {
                        // Ignore messages sent by this peer
                        return;
                    }

                    // Process valid messages
                    if (message.type === "join") {
                        callUser();
                    } else if (message.type === "offer") {
                        handleOffer(message.offer);
                    } else if (message.type === "answer") {
                        logEntry("Receiving Answer");
                        peerRef.current.setRemoteDescription(
                            new RTCSessionDescription(message.answer)
                        );
                    } else if (message.type === "iceCandidate") {
                        logEntry("Receiving and Adding ICE Candidate");
                        try {
                            await peerRef.current.addIceCandidate(message.candidate);
                        } catch (err) {
                            logEntry("Error adding ICE Candidate");
                        }
                    }
                } catch (err) {
                    console.error("Error processing WebSocket message:", err);
                }
            });
        });

        // Cleanup function to close WebRTC and WebSocket connections
        return () => {
            logEntry("Cleaning up resources...");

            // Clear reoffer timeout
            if (reofferTimeout) {
                logEntry("Clearing reoffer timeout");
                clearTimeout(reofferTimeout);
                reofferTimeout = null;
            }

            // Close WebRTC peer connection
            if (peerRef.current) {
                peerRef.current.close();
                peerRef.current = null;
            }

            // Stop all media tracks
            if (userStream.current) {
                userStream.current.getTracks().forEach((track) => track.stop());
                userStream.current = null;
            }

            // Close WebSocket connection
            if (webSocketRef.current) {
                webSocketRef.current.close();
                webSocketRef.current = null;
            }

            logEntry("Resources cleaned up.");
        };
    }, []);

    const handleOffer = async (offer) => {
        logEntry("Received Offer, Creating Answer");
        peerRef.current = createPeer();

        await peerRef.current.setRemoteDescription(
            new RTCSessionDescription(offer)
        );

        await userStream.current.getTracks().forEach((track) => {
            peerRef.current.addTrack(track, userStream.current);
        });

        const answer = await peerRef.current.createAnswer();
        await peerRef.current.setLocalDescription(answer);

        await webSocketRef.current.send(
            JSON.stringify({ type: "answer", answer: peerRef.current.localDescription })
        );

        // Clear the reoffer timeout since an answer has been received
        if (reofferTimeout) {
            logEntry("Clearing reoffer timeout");
            clearTimeout(reofferTimeout);
            reofferTimeout = null;
        }

        // Update connection status
        setConnectionStatus("Peer connected");
    };

    const callUser = async () => {
        logEntry("Calling Other User");
        peerRef.current = createPeer();

        await userStream.current.getTracks().forEach(async (track) => {
            await peerRef.current.addTrack(track, userStream.current);
        });

        // Update connection status
        setConnectionStatus("Calling peer...");
    };

    const createPeer = () => {
        logEntry("Creating Peer Connection");
        const peer = new RTCPeerConnection({
            iceServers: [
                {
                    urls: "stun:stun.relay.metered.ca:80",
                },
                {
                    urls: "turn:global.relay.metered.ca:80",
                    username: "1ff43fbe49b4ef9660cc912c",
                    credential: "rewllWXed9utxN/Q",
                },
                {
                    urls: "turn:global.relay.metered.ca:80?transport=tcp",
                    username: "1ff43fbe49b4ef9660cc912c",
                    credential: "rewllWXed9utxN/Q",
                },
                {
                    urls: "turn:global.relay.metered.ca:443",
                    username: "1ff43fbe49b4ef9660cc912c",
                    credential: "rewllWXed9utxN/Q",
                },
                {
                    urls: "turns:global.relay.metered.ca:443?transport=tcp",
                    username: "1ff43fbe49b4ef9660cc912c",
                    credential: "rewllWXed9utxN/Q",
                },
            ],
        });

        peer.onnegotiationneeded = handleNegotiationNeeded;
        peer.onicecandidate = handleIceCandidateEvent;
        peer.ontrack = handleTrackEvent;

        // Update connection status when peer connection state changes
        peer.onconnectionstatechange = () => {
            if (peer.connectionState === "connected") {
                setConnectionStatus("Peer connected");
            } else if (peer.connectionState === "disconnected" || peer.connectionState === "failed") {
                setConnectionStatus("Peer disconnected");
            }
        };

        return peer;
    };

    let iceCandidateCount = 0;
    const MAX_ICE_CANDIDATES = 50;

    const handleNegotiationNeeded = async () => {
        logEntry("Creating Offer");

        try {
            const myOffer = await peerRef.current.createOffer();
            await peerRef.current.setLocalDescription(myOffer);

            // Send the offer to the signaling server
            webSocketRef.current.send(
                JSON.stringify({ type: "offer", offer: peerRef.current.localDescription })
            );

            // Set a timeout to reoffer if no answer is received
            if (reofferTimeout) {
                logEntry("Clearing reoffer timeout");
                clearTimeout(reofferTimeout); // Clear any existing timeout
            }
            reofferTimeout = setTimeout(() => {
                logEntry("No response to offer. Retrying...");
                handleNegotiationNeeded(); // Retry the offer
            }, REOFFER_TIMEOUT);
        } catch (err) {
            console.error("Error creating or sending offer:", err);
        }
    };

    const handleIceCandidateEvent = async (e) => {
        if (iceCandidateCount >= MAX_ICE_CANDIDATES) {
            console.warn("Max ICE candidates reached");
            return;
        }

        if (e.candidate) {
            iceCandidateCount++;
            webSocketRef.current.send(
                JSON.stringify({ type: "iceCandidate", candidate: e.candidate })
            );
        }
    };

    const handleTrackEvent = (e) => {
        logEntry("Received Tracks");
        logEntry(JSON.stringify(e.streams));
        partnerVideo.current.srcObject = e.streams[0];
    };

    const logEntry = (entry) => {
        const sanitizedEntry = DOMPurify.sanitize(entry); // Sanitize log entry
        const logTextarea = document.querySelector('textarea[name="log"]');
        if (logTextarea) {
            logTextarea.value += sanitizedEntry + '\n';
            logTextarea.scrollTop = logTextarea.scrollHeight;
        }
        console.log(sanitizedEntry);
    };

    return (
        <div>
            <div>
                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        color: "whitesmoke",
                        height: "200px",
                        width: "100%",
                    }}
                >
                    <h1>
                        AVSecure VIP link: https://avsecure.vip{location.pathname}
                    </h1>
                </div>

                {/* Status Display */}
                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        marginBottom: "20px",
                        color: "lime",
                    }}
                >
                    <h2>Status: {connectionStatus}</h2>
                </div>

                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        top: "100px",
                        right: "100px",
                        borderRadius: "10px",
                        overflow: "hidden",
                    }}
                >
                    <video
                        playsInline
                        autoPlay
                        muted
                        controls={true}
                        ref={userVideo}
                        style={{
                            width: isUserVideoSmall ? "25%" : "100%", // Dynamically adjust the width
                            transition: "width 0.3s ease", // Smooth transition
                        }}
                    />
                    <video playsInline autoPlay controls={true} ref={partnerVideo} />
                </div>

                {/* Camera, Microphone, and Video Size Toggle Buttons */}
                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        marginTop: "20px",
                    }}
                >
                    <button onClick={toggleCamera}>
                        {cameraOn ? "Turn Off Camera" : "Turn On Camera"}
                    </button>
                    <button onClick={toggleMicrophone} style={{ marginLeft: "10px" }}>
                        {microphoneOn ? "Mute Microphone" : "Unmute Microphone"}
                    </button>
                    <button onClick={toggleUserVideoSize} style={{ marginLeft: "10px" }}>
                        {isUserVideoSmall ? "Restore Video Size" : "Shrink Video to 25%"}
                    </button>
                </div>

                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        marginTop: "20px",
                        color: "lightgray",
                    }}
                >
                    <p>
                        Note: This is an alpha release for AVSecure.VIP. Please do not use this for any illegal activities. 
                        All calls are encrypted end-to-end, and we do not store any video or audio data.
                    </p>
                    <textarea name="log" rows={4} cols={80} />
                </div>
            </div>
        </div>
    );
};

export default Room;